/**
 * Workflow Library — Persistent storage for saved workflows and macros.
 *
 * Stores workflows in localStorage under `gibbs.workflowLibrary` using
 * the serialization.js v2 format. Provides save, list, load, delete,
 * and re-run with parameter overrides.
 *
 * @module gibbs/workflow/workflowLibrary
 */

import { toJSON, fromJSON } from './serialization';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'gibbs.workflowLibrary';
const WARN_SIZE_BYTES = 4 * 1024 * 1024; // 4 MB
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

// ─── Internal Helpers ─────────────────────────────────────────────────────────

/**
 * Read the full library object from localStorage.
 * @returns {Object} Map of workflow ID to entry objects
 */
function readLibrary() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

/**
 * Write the full library object to localStorage.
 * @param {Object} library
 */
function writeLibrary(library) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(library));
}

/**
 * Check library size and warn/throw as appropriate.
 * @param {Object} library - The library object to check
 * @throws {Error} If serialized size exceeds MAX_SIZE_BYTES
 */
function checkSize(library) {
    const size = JSON.stringify(library).length;
    if (size > MAX_SIZE_BYTES) {
        throw new Error(
            `Workflow library exceeds 5 MB limit (${(size / 1024 / 1024).toFixed(1)} MB). `
            + 'Delete unused workflows before saving.'
        );
    }
    if (size > WARN_SIZE_BYTES) {
        console.warn(
            `Workflow library is large (${(size / 1024 / 1024).toFixed(1)} MB). `
            + 'Consider deleting unused workflows.'
        );
    }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Save a workflow to the library.
 *
 * @param {import('./Workflow.js').Workflow} workflow - Workflow instance to save
 * @param {Object} metadata - Descriptive metadata
 * @param {string} metadata.name - Human-readable workflow name
 * @param {string} [metadata.description] - Optional description
 * @param {string} [metadata.proteinName] - Optional protein name/PDB ID
 * @param {boolean} [metadata.isMacro=false] - Whether this is a recorded macro
 * @returns {string} The generated workflow ID
 * @throws {Error} If saving would exceed the 5 MB localStorage limit
 */
export function saveWorkflow(workflow, metadata = {}) {
    const id = `wf_${Date.now()}`;
    const serialized = toJSON(workflow, {
        id,
        name: metadata.name,
        description: metadata.description,
    });

    const entry = {
        metadata: {
            name: metadata.name || 'Untitled Workflow',
            date: new Date().toISOString(),
            description: metadata.description || '',
            proteinName: metadata.proteinName || '',
            // Default target protein for a protein-bound macro (Path B — where
            // the load is NOT a captured DAG step). Empty for protein-agnostic
            // macros and for Path-A macros (their default lives in the load
            // step's `targetProtein` variable). See the protein-picker plan.
            targetProtein: metadata.targetProtein || '',
            stepCount: workflow.nodeCount,
        },
        workflow: serialized,
        isMacro: metadata.isMacro || false,
    };

    const library = readLibrary();
    library[id] = entry;

    // Size guard: check before persisting
    checkSize(library);

    writeLibrary(library);
    return id;
}

/**
 * List saved workflows with optional name search filter.
 *
 * @param {string} [searchFilter] - Optional substring to match
 *   against workflow names (case-insensitive)
 * @returns {Array<Object>} Workflow metadata entries
 */
export function listWorkflows(searchFilter) {
    const library = readLibrary();
    let entries = Object.entries(library).map(([id, entry]) => ({
        id,
        name: entry.metadata.name,
        date: entry.metadata.date,
        description: entry.metadata.description,
        proteinName: entry.metadata.proteinName,
        targetProtein: entry.metadata.targetProtein || '',
        stepCount: entry.metadata.stepCount,
        isMacro: entry.isMacro || false,
    }));

    if (searchFilter) {
        const filter = searchFilter.toLowerCase();
        entries = entries.filter((e) => e.name.toLowerCase().includes(filter));
    }

    // Sort by date, newest first
    entries.sort((a, b) => new Date(b.date) - new Date(a.date));

    return entries;
}

/**
 * Load a saved workflow by ID and return a deserialized Workflow instance.
 *
 * @param {string} id - Workflow ID (e.g., "wf_1712000000000")
 * @returns {Object|null} { workflow, warnings, metadata }
 *   or null if not found
 */
export function loadWorkflow(id) {
    const library = readLibrary();
    const entry = library[id];
    if (!entry) return null;

    const { workflow, warnings } = fromJSON(entry.workflow);
    return { workflow, warnings, metadata: entry.metadata };
}

/**
 * Delete a workflow from the library.
 *
 * @param {string} id - Workflow ID to delete
 * @returns {boolean} True if the workflow existed and was deleted
 */
export function deleteWorkflow(id) {
    const library = readLibrary();
    if (!library[id]) return false;

    delete library[id];
    writeLibrary(library);
    return true;
}

/**
 * Load a workflow and apply parameter overrides, returning a modified
 * workflow ready for execution.
 *
 * @param {string} id - Workflow ID to load
 * @param {Object} paramChanges - Parameter overrides keyed by nodeId:
 *   `{ nodeId: { paramName: newValue, ... }, ... }`
 * @returns {{ workflow: import('./Workflow.js').Workflow, warnings: string[] } | null}
 *   The modified workflow, or null if the ID is not found
 */
export function prepareRerun(id, paramChanges = {}) {
    const loaded = loadWorkflow(id);
    if (!loaded) return null;

    const { workflow, warnings } = loaded;

    // Apply parameter overrides to matching nodes
    for (const [nodeId, overrides] of Object.entries(paramChanges)) {
        const node = workflow.getNode(nodeId);
        if (!node) {
            warnings.push(`prepareRerun: node "${nodeId}" not found — parameter changes skipped`);
            continue;
        }
        for (const [paramName, newValue] of Object.entries(overrides)) {
            node.paramOverrides[paramName] = newValue;
        }
    }

    return { workflow, warnings };
}

/**
 * Get the current storage usage of the workflow library.
 *
 * @returns {{ bytes: number, megabytes: number, count: number }}
 */
export function getStorageUsage() {
    const raw = localStorage.getItem(STORAGE_KEY) || '{}';
    const library = JSON.parse(raw);
    const bytes = raw.length;
    return {
        bytes,
        megabytes: parseFloat((bytes / 1024 / 1024).toFixed(2)),
        count: Object.keys(library).length,
    };
}
